<template>
  <div class="organization container-fluid">
    <div class="row header-row">
      <div class="col-12 col-md-4 col-lg-6">
        <h1>Test Fixtures</h1>
        <div class="mobile-sort d-md-none">
          <form-component>
            <form>
              <input-select
                id="=selectedOrg"
                v-model="selectedOrg"
                name="selectedOrg"
                data-vv-name="selectedOrg"
                data-vv-as="Organization"
                label="Organization:"
                :options="organizationSelectOptions"
                display-field="name"
                :on-change="onFilterChange"
              />
              <input-select
                id="=selectedSort"
                v-model="selectedSort"
                name="selectedSort"
                data-vv-name="selectedSort"
                data-vv-as="Sorted By:"
                label="Sorted By:"
                type="select"
                :options="sortSelectOptions"
                display-field="display"
                :on-change="onSortChange"
              />
            </form>
          </form-component>
        </div>
      </div>
      <div
        v-if="organizations"
        class=" col-12 col-md-8 col-lg-6"
      >
        <div class="upper-select-container">
          <select
            v-if="$store.getters.isKgbAdminUser"
            v-model="selectedOrg"
            class="upper-select custom-select"
            @change="onFilterChange"
          >
            <option
              v-for="org in organizationSelectOptions"
              :key="org.id"
              :value="org.id"
            >
              {{ org.name }}
            </option>
          </select>
          <select
            v-if="siteSelectOptions && siteSelectOptions.length > 0"
            v-model="selectedSite"
            class="upper-select custom-select"
            @change="onSiteChange"
          >
            <option
              v-for="site in siteSelectOptions"
              :key="site.id"
              :value="site.id"
            >
              {{ site.name }}
            </option>
          </select>
          <button
            v-if="$store.getters.isKgbAdminUser"
            class="btn btn--primary"
            type="button"
            @click="showCreateDeviceModal()"
          >
            <font-awesome-icon
              :icon="['fas', 'plus']"
              class="icon alt"
            />
            New Test Fixture
          </button>
        </div>
        <save-test-fixture-modal
          ref="testFixtureModal"
          :do-cancel="closeTestFixtureModal"
          :do-save-test-fixture="doSaveTestFixture"
          :organizations="organizations"
          :get-organization-sites="getOrganizationSites"
        />
      </div>
    </div>
    <div class="row">
      <div
        v-if="!testFixtures"
        class="col-12"
      >
        <Loader />
      </div>
      <div
        v-else-if="filteredTestFixtures.length === 0"
        class="col-12"
      >
        <div class="no-results text-center">
          <div class="vertical-center">
            No Results Found
          </div>
        </div>
      </div>
      <div
        v-else
        class="col-12"
      >
        <div>
          <responsive-table>
            <test-fixture-table
              ref="testFixtureTable"
              :test-fixtures="filteredTestFixtures"
              :latest-test-fixture-version="latestTextFixtureVersion"
              :enable-test-fixture="enableTestFixture"
              :show-edit-modal="showEditDeviceModal"
              :test-fixture-checkins="getTestFixtureCheckins"
              :update-to-latest-version="updateToLatestVersion"
            />
          </responsive-table>
        </div>
      </div>
    </div>

    <confirm-modal
      ref="confirmModal"
      :title="confirmTitle"
      :body="confirmBody"
      :on-confirm="confirmAction"
    />
  </div>
</template>

<script>
/* eslint-disable camelcase */
import FormComponent from '@/components/FormComponent.vue';
import Loader from '@/components/Loader.vue';
import ResponsiveTable from '@/components/ResponsiveTable.vue';
import InputSelect from '@/components/inputs/InputSelect.vue';
import ConfirmModal from '@/components/modals/ConfirmModal.vue';
import SaveTestFixtureModal from '@/components/modals/SaveTestFixtureModal.vue';
import TestFixtureTable from '@/components/tables/TestFixturesTable.vue';

import UserClient from '@/api/UserClient';

export default {
  name: 'TestFixtures',
  components: {
    ResponsiveTable,
    TestFixtureTable,
    SaveTestFixtureModal,
    FormComponent,
    InputSelect,
    Loader,
    ConfirmModal,
  },
  data() {
    return {
      selectedTestFixture: null,
      userClient: new UserClient(this.$http),
      testFixtures: null,
      organizations: [],
      sites: [],
      selectedSite: 'all',
      selectedOrg: 'all',
      selectedSort: 'created_by',

      confirmTitle: '',
      confirmBody: '',
      latestTextFixtureVersion: '',
      confirmAction: () => {},

      sortSelectOptions: [{
        id: 'created_by', display: 'Create Date',
      }, {
        id: 'serial_number', display: 'Serial ID',
      }, {
        id: 'organization_name', display: 'Organization',
      },
      ],
    };
  },
  computed: {
    organizationSelectOptions() {
      const options = [{ id: 'all', name: 'All Organizations' }];
      if (!this.organizations) {
        return options;
      }
      return [...options, ...this.organizations.map(org => ({
        id: `${org.id}`,
        name: org.name,
      }))];
    },
    siteSelectOptions() {
      const options = [{ id: 'all', name: 'All Sites' }, { id: '-1', name: 'Primary' }];
      if (!this.sites) {
        return [];
      }
      return [...options, ...this.sites.map(site => ({ ...site, id: `${site.id}` }))];
    },
    filteredTestFixtures() {
      if (this.selectedSite === 'all' || !this.selectedSite) {
        return this.testFixtures;
      } if (`${this.selectedSite}` === '-1') {
        return this.testFixtures.filter(fixture => fixture.site === null);
      }
      const selectedSiteId = Number(this.selectedSite);
      return this.testFixtures.filter(fixture => fixture.site === selectedSiteId);
    },
  },
  watch: {
    // eslint-disable-next-line func-names
    '$route.params': function () {
      const newSelectedOrg = this.$route.query.org ? this.$route.query.org : 'all';
      if (newSelectedOrg !== this.selectedOrg) {
        this.selectedOrg = newSelectedOrg;
        this.getTestFixtures();
        this.getSites();
      }
    },
  },
  mounted() {
    if (this.$store.getters.isKgbAdminUser) {
      this.userClient.getOrganizations().then((orgs) => {
        orgs.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
        this.organizations = orgs.filter(x => !x.is_removed);
      }).catch(() => {
        this.organizations = [];
      });
    }
    this.selectedOrg = this.$route.query.org ? this.$route.query.org : 'all';
    this.getTestFixtures();
    this.getSites();
    this.userClient.getCurrentMenderTestFixtureVersion().then((currentVersion) => {
      this.latestTextFixtureVersion = currentVersion.name;
    });
  },
  methods: {
    getTestFixtures() {
      this.testFixtures = null;

      let organizationFilter = '';
      if (!this.$store.getters.isKgbAdminUser) {
        organizationFilter = this.$store.getters.currentUser.organization_id || -1;
      } else if (this.selectedOrg !== 'all') {
        organizationFilter = this.selectedOrg;
        this.$router.push({ query: { ...this.$route.query, org: organizationFilter, p: 1 } });
      } else {
        this.$router.push({ query: { ...this.$route.query, org: undefined, p: 1 } });
      }

      this.userClient.getTestFixtures(organizationFilter).then((testFixtures) => {
        this.testFixtures = testFixtures;
      }).catch(() => {
        this.testFixtures = [];
      });
    },
    getOrganizationSites(organizationId) {
      return this.userClient.getSitesForOrganization(organizationId);
    },
    async getSites() {
      this.sites = null;
      const organizationFilter = (!this.$store.getters.isKgbAdminUser)
        ? this.$store.getters.currentUser.organization_id
        : this.selectedOrg;
      if (organizationFilter === 'all') return;
      this.sites = await this.userClient.getSitesForOrganization(organizationFilter);
    },
    onFilterChange() {
      this.testFixtures = null;
      this.getTestFixtures();
      this.selectedSite = 'all';
      this.getSites();
    },
    onSortChange() {
      this.$refs.testFixtureTable.sortTable(this.selectedSort, true);
    },
    onSiteChange() {
      this.$router.push({ query: { ...this.$route.query, site: this.selectedSite === 'all' ? undefined : this.selectedSite, p: 1 } });
    },
    getTestFixtureCheckins(testFixtureId) {
      return this.userClient
        .getTestFixtureCheckins(testFixtureId).then(x => x.results).catch(() => []);
    },
    enableTestFixture(testFixture) {
      this.selectedTestFixture = testFixture;
      this.confirmTitle = 'Enable Test Fixture';
      this.confirmBody = 'Are you sure you want to enable this test fixture?';
      this.confirmAction = this.doEnableTestFixture;
      this.$refs.confirmModal.show();
    },
    doEnableTestFixture(whenDone) {
      if (this.selectedTestFixture) {
        this.userClient.enableTestFixture(this.selectedTestFixture.id).then(() => {
          this.$refs.confirmModal.hide();
          this.getTestFixtures();

          this.$store.commit('setGlobalAlert', {
            msg: 'Test Fixture enabled!',
          });
          this.selectedTestFixture = null;
          whenDone();
        }).catch(() => {
          this.$store.commit('setGlobalAlert', {
            msg: 'Unable to enable test fixture. Please try again!', error: true,
          });
          whenDone();
        });
        return false;
      }
      return true;
    },
    showCreateDeviceModal() {
      this.$refs.testFixtureModal.$children[0].show();
      let defaultOrganizationId = null;
      if (this.selectedOrg !== 'all') {
        defaultOrganizationId = this.selectedOrg;
      } else {
        const foundKgbOrg = this.organizations.find(x => x.name.startsWith('KGB'));
        if (foundKgbOrg) {
          defaultOrganizationId = `${foundKgbOrg.id}`;
        }
      }

      this.$refs.testFixtureModal.setDeviceModal({
        organization: defaultOrganizationId,
      });
    },
    showEditDeviceModal(testFixture) {
      this.$refs.testFixtureModal.$children[0].show();
      this.$refs.testFixtureModal.setDeviceModal(testFixture);
    },
    closeTestFixtureModal() {
      this.$refs.testFixtureModal.$children[0].hide();
    },

    updateToLatestVersion(testFixture) {
      this.userClient.updateTestFixtureToLatestSoftwareVersion(testFixture.id).then(() => {
        this.$store.commit('setGlobalAlert', {
          msg: `Successfully sent request to update test fixture ${testFixture.serialNumber} to ${this.latestTextFixtureVersion} version`,
        });
      }).catch(() => {
        this.$store.commit('setGlobalAlert', {
          msg: `Error sending request to update test fixture ${testFixture.serialNumber} to ${this.latestTextFixtureVersion} version`, error: true,
        });
      });
    },
    doSaveTestFixture(id, data, whenDone) {
      if (id) {
        this.userClient.updateTestFixture(id, data).then(() => {
          this.closeTestFixtureModal();
          this.getTestFixtures();

          this.$store.commit('setGlobalAlert', {
            msg: 'Test Fixture saved!',
          });
        }).catch(() => {
          this.$store.commit('setGlobalAlert', {
            msg: 'Unable to save test fixture. Please try again!', error: true,
          });
          whenDone();
        });
      } else {
        this.userClient.createTestFixture(data).then(() => {
          this.closeTestFixtureModal();
          this.getTestFixtures();

          this.$store.commit('setGlobalAlert', {
            msg: 'Test Fixture saved!',
          });
        }).catch(() => {
          this.$store.commit('setGlobalAlert', {
            msg: 'Unable to save test fixture. Please try again!', error: true,
          });
          whenDone();
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.upper-select-container {
  display: flex;
  justify-content: right;

  & .btn--primary {
    flex-grow: 0;
    flex-shrink: 0;
    width: 190px;
    margin-left: 35px;
  }
}
</style>
